import { useState } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Loader2, AlertTriangle, CheckCircle2, XCircle, Shield, Info,
} from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";
import BonferroniAdvisory from "./BonferroniAdvisory";

const TIME_INDEX_KEYWORDS = ["date", "time", "week", "month", "year", "quarter", "period", "timestamp"];

function validateTestStructure(testType, colA, colB, dataset) {
  if (!dataset || !colA) return null;
  const isTimeIndex = (col) => TIME_INDEX_KEYWORDS.some(kw => col.toLowerCase().includes(kw));
  if (testType === "t_test") {
    if (isTimeIndex(colA)) return `'${colA}' appears to be a time index.`;
    if (colB && isTimeIndex(colB)) return `'${colB}' appears to be a time index.`;
    if (colB && colA === colB) return "Column A and Column B cannot be the same variable.";
  }
  if (testType === "correlation") {
    if (colB && colA === colB) return "Column A and Column B cannot be the same variable.";
    if (isTimeIndex(colA)) return `'${colA}' appears to be a time index.`;
    if (colB && isTimeIndex(colB)) return `'${colB}' appears to be a time index.`;
  }
  return null;
}

function getSuggestedTest(testType, colA, colB, dataset) {
  if (!dataset || !colA || !colB) return null;
  const data = dataset.data || [];
  if (data.length === 0) return null;
  const isTimeIndex = (col) => TIME_INDEX_KEYWORDS.some(kw => col.toLowerCase().includes(kw));
  if (isTimeIndex(colA) || isTimeIndex(colB)) return { test: "regression", reason: "Time index detected \u2014 consider Trend / Regression analysis." };
  const isNum = (col) => { try { return data.every(row => !isNaN(Number(row[col])) || row[col] === null || row[col] === ""); } catch { return false; } };
  const uniqueCount = (col) => new Set(data.map(row => row[col]).filter(v => v != null)).size;
  if (testType === "t_test" && isNum(colA) && isNum(colB)) return { test: "correlation", reason: "Two numeric columns detected. Switch to Correlation or Regression." };
  if (testType === "t_test" && uniqueCount(colB) > 2) return { test: "anova", reason: `Grouping column '${colB}' has ${uniqueCount(colB)} levels (>2). Switch to One-Way ANOVA.` };
  return null;
}

export default function ManualTestsTab({ testType, setTestType, selectedDataset, datasets, setSelectedDataset, columnA, setColumnA, columnB, setColumnB, alpha, setAlpha, testResult, setTestResult, analyzing, runHypothesisTest, getNumericColumns, getCategoricalColumns, projectId, token }) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  const structError = validateTestStructure(testType, columnA, columnB, selectedDataset);
  const suggestion = getSuggestedTest(testType, columnA, columnB, selectedDataset);
  const canRun = selectedDataset && columnA && !structError && !analyzing;
  const hasAssumptionFailures = testResult?.assumptions && Object.values(testResult.assumptions).some(v => v?.status === "fail");

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="manual-tests-tab">
      <h3 className="font-heading font-semibold mb-4">Hypothesis Testing</h3>

      <div className="p-3 mb-5 bg-muted/30 border border-border rounded-lg" data-testid="hypothesis-governance-banner">
        <div className="flex items-start gap-2"><Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /><p className="text-xs text-muted-foreground">Statistical significance does not imply causation or business impact. Results must be interpreted within process context and validated against operational knowledge.</p></div>
      </div>

      {projectId && token && (
        <div className="mb-5">
          <BonferroniAdvisory projectId={projectId} token={token} currentAlpha={alpha} refreshKey={testResult?.metadata?.timestamp} />
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-4 mb-4">
        <div className="space-y-2">
          <Label>Test Type</Label>
          <select value={testType} onChange={(e) => { setTestType(e.target.value); setTestResult(null); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="test-type-select">
            <option value="t_test">T-Test</option><option value="chi_square">Chi-Square</option><option value="anova">ANOVA</option><option value="correlation">Correlation</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label>Dataset</Label>
          <select value={selectedDataset?.dataset_id || ""} onChange={(e) => { const ds = datasets.find((d) => d.dataset_id === e.target.value); setSelectedDataset(ds); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
            <option value="">Select dataset</option>
            {datasets.map((ds) => <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Column A {testType === "t_test" ? "(DV)" : testType === "anova" ? "(Grouping or DV)" : ""}</Label>
          <select value={columnA} onChange={(e) => setColumnA(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" disabled={!selectedDataset} data-testid="column-a-select">
            <option value="">Select column</option>
            {(testType === "chi_square" ? getCategoricalColumns() : testType === "anova" ? [...getCategoricalColumns(), ...getNumericColumns()] : getNumericColumns()).map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Column B {testType === "t_test" ? "(Grouping)" : testType === "anova" ? "(DV or Grouping)" : ""}</Label>
          <select value={columnB} onChange={(e) => setColumnB(e.target.value)} className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${structError ? "border-red-500/50" : "border-input"}`} disabled={!selectedDataset} data-testid="column-b-select">
            <option value="">Select column</option>
            {(testType === "chi_square" ? getCategoricalColumns() : testType === "t_test" || testType === "anova" ? [...getCategoricalColumns(), ...getNumericColumns()] : getNumericColumns()).map((col) => <option key={col} value={col}>{col}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={runHypothesisTest} disabled={!canRun} className="w-full" data-testid="run-test-btn">{analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run Test"}</Button>
        </div>
      </div>

      {structError && (
        <div className="p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="struct-validation-error">
          <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {structError}</p>
        </div>
      )}
      {suggestion && !structError && (
        <div className="p-3 mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="test-suggestion">
          <p className="text-xs text-amber-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5 shrink-0" /> {suggestion.reason}</p>
        </div>
      )}

      <div className="mb-5">
        <Label>Significance Level ({"\u03B1"})</Label>
        <div className="flex gap-2 mt-1">
          {[0.01, 0.05, 0.10].map((a) => <Button key={a} variant={alpha === a ? "default" : "outline"} size="sm" onClick={() => setAlpha(a)} data-testid={`alpha-${a}`}>{a}</Button>)}
        </div>
      </div>

      {testResult && (
        <div className="space-y-4" data-testid="test-results">
          <div className={`p-4 rounded-lg border-2 ${testResult.significant ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-muted/30"}`} data-testid="test-decision-banner">
            <div className="flex items-center gap-2 mb-1">
              {testResult.significant ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-muted-foreground" />}
              <span className="font-semibold">{testResult.test_name}</span>
            </div>
            <p className={`text-sm font-medium ${testResult.significant ? "text-emerald-400" : "text-muted-foreground"}`} data-testid="test-decision">{testResult.decision}</p>
          </div>

          {testResult.assumptions && Object.keys(testResult.assumptions).length > 0 && (
            <CollapsibleSection title={<>Assumption Checks {hasAssumptionFailures && <span className="text-[10px] text-amber-400 ml-1">(violations detected)</span>}</>} icon={<Shield className="w-4 h-4 text-primary" />} open={assumptionsOpen} onToggle={() => setAssumptionsOpen(v => !v)} testId="test-assumptions">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(testResult.assumptions).map(([key, val]) => {
                  if (typeof val !== "object" || val === null) return null;
                  const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                  if (val.status) {
                    const pass = val.status === "pass";
                    return (
                      <div key={key} className={`p-3 rounded-lg border ${pass ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className={`text-sm font-semibold ${pass ? "text-emerald-400" : "text-amber-400"}`}>{pass ? "Pass" : "Fail"}</p>
                        {val.p_value != null && <p className="text-[10px] text-muted-foreground">p = {val.p_value} ({val.test})</p>}
                        {val.detail && <p className="text-[10px] text-muted-foreground">{val.detail}</p>}
                      </div>
                    );
                  }
                  if (val.n != null) return (
                    <div key={key} className={`p-3 rounded-lg border ${val.adequate ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className={`text-sm font-semibold ${val.adequate ? "text-emerald-400" : "text-amber-400"}`}>n = {val.n} {val.adequate ? "" : "(Warning)"}</p>
                    </div>
                  );
                  return null;
                })}
              </div>
              {hasAssumptionFailures && <p className="text-xs text-amber-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Assumption violations detected \u2014 interpret with caution.</p>}
            </CollapsibleSection>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="test-stats-grid">
            {testResult.t_statistic != null && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">t-Statistic</p><p className="text-lg font-bold font-mono">{testResult.t_statistic}</p></div>}
            {testResult.chi2_statistic != null && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">{"\u03C7\u00B2"} Statistic</p><p className="text-lg font-bold font-mono">{testResult.chi2_statistic}</p></div>}
            {testResult.f_statistic != null && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">F-Statistic</p><p className="text-lg font-bold font-mono">{testResult.f_statistic}</p></div>}
            {testResult.correlation != null && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Correlation (r)</p><p className="text-lg font-bold font-mono">{testResult.correlation}</p></div>}
            {testResult.degrees_of_freedom != null && <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Degrees of Freedom</p><p className="text-lg font-bold font-mono">{testResult.degrees_of_freedom}</p></div>}
            <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">P-Value</p><p className={`text-lg font-bold font-mono ${(testResult.p_value_raw ?? testResult.p_value) < alpha ? "text-emerald-400" : ""}`} data-testid="test-pvalue">{testResult.p_value_display || testResult.p_value}</p></div>
            <div className="bg-background border border-border rounded-lg p-3"><p className="text-xs text-muted-foreground">Alpha ({"\u03B1"})</p><p className="text-lg font-bold font-mono">{alpha}</p></div>
            {testResult.effect_size && (
              <div className="bg-background border border-border rounded-lg p-3" data-testid="test-effect-size">
                <p className="text-xs text-muted-foreground">Effect Size</p>
                <p className="text-lg font-bold font-mono">{testResult.effect_size.cohens_d ?? testResult.effect_size.cramers_v ?? testResult.effect_size.eta_squared ?? testResult.effect_size.r_squared}</p>
                <p className={`text-xs ${testResult.effect_size.interpretation === "Large" ? "text-emerald-400" : testResult.effect_size.interpretation === "Medium" ? "text-amber-400" : "text-muted-foreground"}`}>{testResult.effect_size.interpretation}</p>
              </div>
            )}
          </div>

          {testResult.confidence_interval && (
            <div className="bg-background border border-border rounded-lg p-3" data-testid="test-ci">
              <p className="text-xs text-muted-foreground">{testResult.confidence_interval.level} Confidence Interval</p>
              <p className="font-semibold font-mono">[{testResult.confidence_interval.lower}, {testResult.confidence_interval.upper}]</p>
            </div>
          )}

          {testResult.group_stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testResult.group_stats.group_a && (
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Group A ({testResult.group_stats.group_a.label || columnA})</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Mean:</span> <span className="font-mono">{testResult.group_stats.group_a.mean}</span></div>
                    <div><span className="text-muted-foreground">Std:</span> <span className="font-mono">{testResult.group_stats.group_a.std}</span></div>
                    <div><span className="text-muted-foreground">N:</span> <span className="font-mono">{testResult.group_stats.group_a.n}</span></div>
                  </div>
                </div>
              )}
              {testResult.group_stats.group_b && (
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Group B ({testResult.group_stats.group_b.label || columnB})</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Mean:</span> <span className="font-mono">{testResult.group_stats.group_b.mean}</span></div>
                    <div><span className="text-muted-foreground">Std:</span> <span className="font-mono">{testResult.group_stats.group_b.std}</span></div>
                    <div><span className="text-muted-foreground">N:</span> <span className="font-mono">{testResult.group_stats.group_b.n}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-card border border-border rounded-lg p-4" data-testid="test-interpretation">
            <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Interpretation</h4>
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">H{"\u2080"} (Null):</span> <span className="ml-1">No statistically significant difference or relationship exists.</span></div>
              <div><span className="text-muted-foreground">H{"\u2081"} (Alternative):</span> <span className="ml-1">A statistically significant difference or relationship exists.</span></div>
              <div className="pt-1 border-t border-border">
                <span className="text-muted-foreground">Finding:</span>
                <span className={`ml-1 font-medium ${testResult.significant ? "text-emerald-400" : "text-muted-foreground"}`}>
                  {testResult.significant
                    ? `At \u03B1 = ${alpha}, sufficient evidence exists to reject H\u2080. The observed effect is statistically significant.`
                    : `At \u03B1 = ${alpha}, insufficient evidence to reject H\u2080. The observed difference is not statistically significant.`
                  }
                </span>
              </div>
              {hasAssumptionFailures && <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Assumption violations detected \u2014 interpret with caution.</p>}
            </div>
          </div>

          {testResult.metadata && (
            <CollapsibleSection title="Analysis Metadata" icon={<Info className="w-4 h-4 text-primary" />} open={metadataOpen} onToggle={() => setMetadataOpen(v => !v)} testId="test-analysis-metadata">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-testid="test-metadata-grid">
                <div><span className="text-muted-foreground">Dataset:</span> <span className="font-medium ml-1">{testResult.metadata.dataset_name}</span></div>
                <div><span className="text-muted-foreground">Column A:</span> <span className="font-medium ml-1">{testResult.metadata.column_a}</span></div>
                <div><span className="text-muted-foreground">Column B:</span> <span className="font-medium ml-1">{testResult.metadata.column_b || "N/A"}</span></div>
                <div><span className="text-muted-foreground">Alpha:</span> <span className="font-medium ml-1">{testResult.metadata.alpha}</span></div>
                <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono text-muted-foreground ml-1">{testResult.metadata.timestamp?.split("T")[0]}</span></div>
                <div><span className="text-muted-foreground">Engine:</span> <span className="font-mono text-muted-foreground ml-1">{testResult.metadata.engine_version}</span></div>
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}
    </div>
  );
}
